/**
 * Mock Description Scenarios
 *
 * Provides the four 2×2 combinations of descriptionType × requireDescription
 * that the daily-descriptions feature introduces.  Import these in tests,
 * stories, or anywhere you need to exercise a specific mode.
 *
 * Usage:
 *   import { DESCRIPTION_SCENARIOS, addMockDailyNotes } from '@/data/mockDescriptionScenarios';
 *   const settings = { ...DEFAULT_TIMESHEET_SETTINGS, ...DESCRIPTION_SCENARIOS.requiredDaily };
 */

import type { DayKey, TimesheetEntry } from '@/types/timesheet';

// ─── Four scenario overrides ────────────────────────────────────────────────

/**
 * Partial settings objects — spread over DEFAULT_TIMESHEET_SETTINGS (or any
 * full OrganisationTimesheetSettings object) to activate a scenario.
 */
export const DESCRIPTION_SCENARIOS = {
  /**
   * Scenario 1 — Current default (no visible change for existing organisations)
   * Row textarea shown, required before submit.
   */
  currentDefault: {
    descriptionType: 'row' as const,
    requireDescription: 'always' as const,
  },

  /**
   * Scenario 2 — Optional row
   * Row textarea shown, but submission is never blocked.
   */
  optionalRow: {
    descriptionType: 'row' as const,
    requireDescription: 'never' as const,
  },

  /**
   * Scenario 3 — Optional daily
   * Note icon on each hours cell; popover opens on deliberate click only.
   * No auto-open on tab/blur.
   */
  optionalDaily: {
    descriptionType: 'daily' as const,
    requireDescription: 'never' as const,
  },

  /**
   * Scenario 4 — Required daily
   * Note icon on each hours cell; popover auto-opens when the user tabs/clicks
   * away from a newly filled hours cell.  Submit blocked until all cells with
   * hours have a note.
   */
  requiredDaily: {
    descriptionType: 'daily' as const,
    requireDescription: 'always' as const,
  },
} as const;

// ─── Sample daily note copy ──────────────────────────────────────────────────

const SAMPLE_NOTES: string[] = [
  'Stakeholder workshop for Q2 planning',
  'Development sprint — feature branch implementation',
  'Code review and PR sign-off',
  'Bug investigation and hotfix deployment',
  'Documentation and knowledge-base updates',
  'Architecture discussion with tech lead',
  'Client call — requirements clarification',
  'Unit testing and coverage improvements',
  'Database schema review',
  'Retrospective and sprint planning',
];

function pickNote(): string {
  return SAMPLE_NOTES[Math.floor(Math.random() * SAMPLE_NOTES.length)];
}

// ─── Helper: enrich entries with dailyNotes ──────────────────────────────────

const ALL_DAYS: DayKey[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

/**
 * Returns a new copy of the entry array with `dailyNotes` populated for use
 * in daily-mode scenarios.
 *
 * Rules applied deterministically per entry + day so the data is stable
 * across re-renders (no Math.random() in the render path):
 *
 *  - Days with hours === 0 → no note (icon not shown)
 *  - ~60 % of days with hours → note present   (filled icon state)
 *  - ~40 % of days with hours → no note        (empty icon state, required or optional)
 *
 * The 60 / 40 split is derived from `(entryId + dayIndex) % 5 >= 2` so it is
 * deterministic without seeding a PRNG.
 */
export function addMockDailyNotes(entries: TimesheetEntry[]): TimesheetEntry[] {
  return entries.map(entry => {
    const dailyNotes: Partial<Record<DayKey, string>> = {};

    ALL_DAYS.forEach((day, dayIndex) => {
      const hasHours = (entry.hours[day] ?? 0) > 0;
      if (!hasHours) return; // No icon, no note needed

      // Deterministic fill: ~60 % get a note
      const shouldFill = (Math.abs(entry.id) + dayIndex) % 5 >= 2;
      if (shouldFill) {
        // Pick a note deterministically from SAMPLE_NOTES
        const noteIndex = (Math.abs(entry.id) + dayIndex) % SAMPLE_NOTES.length;
        dailyNotes[day] = SAMPLE_NOTES[noteIndex];
      }
      // else: leave undefined → empty icon state
    });

    return { ...entry, dailyNotes };
  });
}

/**
 * Convenience: returns a copy of a single entry with all days-with-hours
 * populated with notes (useful for "all filled" test cases).
 */
export function fillAllDailyNotes(entry: TimesheetEntry): TimesheetEntry {
  const dailyNotes: Partial<Record<DayKey, string>> = {};
  ALL_DAYS.forEach(day => {
    if ((entry.hours[day] ?? 0) > 0) {
      dailyNotes[day] = pickNote();
    }
  });
  return { ...entry, dailyNotes };
}

/**
 * Convenience: returns a copy of a single entry with all dailyNotes cleared
 * (useful for "none filled" / empty-state test cases).
 */
export function clearAllDailyNotes(entry: TimesheetEntry): TimesheetEntry {
  return { ...entry, dailyNotes: {} };
}
